import React, { useEffect, useState } from 'react';
import Icons from 'assets/icons';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';

interface VimeoOEmbedData {
  html?: string;
  title?: string;
  [key: string]: unknown;
}

const extractIframeSrc = (htmlString: string): string | null => {
  const regex = /<iframe[^>]+src="([^"]+)"[^>]*><\/iframe>/;

  const match = regex.exec(htmlString);

  return match ? match[1] : null;
};

interface VimeoComponentProps {
  vimeoLink?: string;
}

const VimeoComponent = ({
  vimeoLink = '',
}: VimeoComponentProps): React.JSX.Element => {
  const [finalLink, setFinalLink] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('Vimeo video');

  useEffect(() => {
    if (!vimeoLink) {
      return;
    }

    fetchVimeoOEmbedData(vimeoLink)
      .then((oEmbedData) => {
        if (oEmbedData?.html) {
          const iframeSrc = extractIframeSrc(oEmbedData.html);

          if (iframeSrc) {
            setFinalLink(iframeSrc);
          }
        }
        if (oEmbedData?.title) {
          setTitle(oEmbedData.title);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [vimeoLink]);

  const fetchVimeoOEmbedData = async (
    videoUrl: string
  ): Promise<VimeoOEmbedData | undefined> => {
    const oEmbedEndpoint = 'https://vimeo.com/api/oembed.json';
    const requestUrl = `${oEmbedEndpoint}?url=${encodeURIComponent(videoUrl)}`;

    setLoading(true);
    try {
      const response = await fetch(requestUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = (await response.json()) as VimeoOEmbedData;

      return data;
    } catch (error) {
      console.error('Error fetching Vimeo oEmbed data:', error);

      return undefined;
    } finally {
      setLoading(false);
    }
  };
  const containerStyles =
    'w-full h-auto mobile:h-[194px] mobile:w-[345px] tablet:h-[394px] tablet:w-[700px] xs:h-[440px] xs:w-[780px]';

  return (
    <Box
      sx={{
        width: '100%',
        '@media (min-width: 375px)': { mb: 3 },
        '@media (min-width: 960px)': { mb: 4 },
      }}
    >
      {finalLink ? (
        <iframe
          className={containerStyles}
          // Native width/height (not just the responsive Tailwind classes
          // above) so the player keeps a usable intrinsic size — including
          // its own controls — if the page's CSS fails to load or is
          // disabled (WCAG 1.3.2 / positioning-with-CSS). CSS still drives
          // the actual responsive size in normal operation.
          width={780}
          height={440}
          src={finalLink}
          allow="autoplay; fullscreen"
          allowFullScreen
          title={title}
        ></iframe>
      ) : (
        !finalLink && (
          <div className={`${containerStyles} bg-navy-50 relative`}>
            <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-full">
              <Loader loading={loading} fullScreen={false} overlay="navy" />

              <Stack sx={{ alignItems: 'center', gap: 2.5, width: '100%' }}>
                <Icons.BrokenLink className="mobile:w-16 mobile:h-16 xs:w-20 xs:h-20" />
                <span className="text-gray-600 mobile:text-sm md:text-lg font-semibold">
                  Looks like the vimeo video link is broken.
                </span>
              </Stack>
            </div>
          </div>
        )
      )}
    </Box>
  );
};

export default VimeoComponent;
